import { SelectSimple } from '@advanza/input'
import { Button } from '@advanza/ui'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import TextAreaField from '@trustoo/components/input/TextAreaField'
import BaseModal, {
    BaseModalFooter,
    BaseModalHeader,
    BaseModalMainContent,
} from '@trustoo/components/modal/BaseModal'
import { fetchCancelReasons } from 'actions/providers'
import { scheduleTermination } from 'actions/scheduledTerminations'
import DatePicker from 'components/DatePicker'
import { addMonths, format, isAfter, isValid, parseISO, startOfDay } from 'date-fns'
import { useAppDispatch, useAppSelector } from 'hooks/hooks'
import { useProvider } from 'hooks/providerHooks'
import { useEffect, useState } from 'react'

// Reasons at or above this value are "initiated by us" and are not offered here, because this flow
// only covers providers who asked to churn themselves.
const CANCEL_REASON_UNSUBSCRIBED_BY_US = 101
const CANCEL_REASON_OTHER = 10
const MAX_MONTHS_AHEAD = 6
const DATE_VALUE_FORMAT = 'yyyy-MM-dd'

interface Props {
    providerId: number
    onScheduled?: () => void
}

const ScheduleTerminationModal = NiceModal.create(({ providerId, onScheduled }: Props) => {
    const dispatch = useAppDispatch()
    const modal = useModal()
    const { provider } = useProvider(providerId)
    const cancelReasons: Record<number, string> =
        useAppSelector((state) => state.cancellations.extra.cancelReasons) || {}
    const today = startOfDay(new Date())
    const [reason, setReason] = useState<string>('')
    const [explanation, setExplanation] = useState('')
    const [terminationDate, setTerminationDate] = useState(format(today, DATE_VALUE_FORMAT))
    const [isConfirming, setIsConfirming] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        dispatch(fetchCancelReasons())
    }, [dispatch])

    const parsedDate = parseISO(terminationDate)
    const isValidDate = isValid(parsedDate)
    const isImmediate = isValidDate && !isAfter(parsedDate, today)
    const providerName = provider?.business_name ?? 'This provider'

    const reasonOptions = Object.entries(cancelReasons)
        .map(([value, name]) => ({ value, name }))
        .filter(({ value }) => parseInt(value, 10) < CANCEL_REASON_UNSUBSCRIBED_BY_US)
        .sort(
            ({ value: valueA }, { value: valueB }) =>
                Number(parseInt(valueA, 10) === CANCEL_REASON_OTHER) -
                Number(parseInt(valueB, 10) === CANCEL_REASON_OTHER),
        )

    const onClose = () => {
        modal.resolve(false)
        modal.hide()
    }

    const onContinue = () => {
        if (!reason) {
            setError('Please select a churn reason')
            return
        }

        if (!isValidDate) {
            setError('Please pick a termination date')
            return
        }

        setError(null)
        setIsConfirming(true)
    }

    const onConfirm = async () => {
        setIsSaving(true)
        setError(null)

        try {
            await scheduleTermination(
                providerId,
                parseInt(reason, 10),
                explanation.trim() || null,
                terminationDate,
            )

            if (onScheduled) {
                onScheduled()
            }

            modal.resolve(true)
            modal.hide()
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to schedule the termination')
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <BaseModal onClose={onClose} maxWidth={520}>
            <BaseModalHeader>
                <h2>{isConfirming ? 'Confirm termination' : 'Schedule termination'}</h2>
            </BaseModalHeader>
            <BaseModalMainContent>
                {isConfirming ? (
                    <div className="space-y-4">
                        <p>
                            {isImmediate ? (
                                <>
                                    <b>{providerName}</b> is terminated <b>straight away</b>: their
                                    subscription is paused, lead sending stops, their profile is
                                    deactivated and the cancellation confirmation email is sent.{' '}
                                    <b>This cannot be undone.</b>
                                </>
                            ) : (
                                <>
                                    <b>{providerName}</b> stays fully active until the termination
                                    date. You can undo the termination up until then.
                                </>
                            )}
                        </p>
                        <div className="p-2 bg-tertiary-50 border border-tertiary-200 rounded-md">
                            <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
                                <div className="font-bold">Termination date:</div>
                                <div className="font-bold text-tertiary-500">
                                    {format(parsedDate, 'd MMMM yyyy')}
                                    {isImmediate && ' (today)'}
                                </div>
                                <div className="font-bold">Reason:</div>
                                <div>{cancelReasons[parseInt(reason, 10)]}</div>
                                {explanation.trim() !== '' && (
                                    <>
                                        <div className="font-bold">Note:</div>
                                        <div>{explanation.trim()}</div>
                                    </>
                                )}
                            </div>
                        </div>
                        {error && (
                            <div className="text-md text-red-600 bg-red-50 p-2 rounded">
                                {error}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="space-y-4">
                        <p>
                            {(() => {
                                if (!isValidDate) {
                                    return 'Pick the date the provider should be terminated on.'
                                }

                                if (isImmediate) {
                                    return (
                                        <>
                                            The provider is terminated <b>straight away</b>: their
                                            subscription is paused, lead sending stops, their
                                            profile is deactivated and the cancellation confirmation
                                            email is sent.
                                        </>
                                    )
                                }

                                return (
                                    <>
                                        The provider stays fully active until{' '}
                                        <b>{format(parsedDate, 'd MMMM yyyy')}</b>. On that date
                                        their subscription is paused, lead sending stops, their
                                        profile is deactivated and the cancellation confirmation
                                        email is sent. You can undo the termination up until then.
                                    </>
                                )
                            })()}
                        </p>
                        <div>
                            <label className="block text-md font-bold text-neutral-700 mb-1">
                                Termination date *
                            </label>
                            <DatePicker
                                value={terminationDate}
                                format="dd-MM-yyyy"
                                error={!isValidDate}
                                dayPickerProps={{
                                    disabled: {
                                        before: today,
                                        after: addMonths(today, MAX_MONTHS_AHEAD),
                                    },
                                }}
                                onChange={({ target }) => {
                                    setTerminationDate(target.value || '')
                                    setError(null)
                                }}
                            />
                        </div>
                        <div>
                            <label className="block text-md font-bold text-neutral-700 mb-1">
                                Churn reason *
                            </label>
                            <SelectSimple
                                value={reason}
                                emptyOption=" "
                                options={reasonOptions}
                                disabled={isSaving}
                                onChange={(e) => {
                                    setReason(String(e.target.value || ''))
                                    setError(null)
                                }}
                            />
                        </div>
                        <div>
                            <label className="block text-md font-bold text-neutral-700 mb-1">
                                Note
                            </label>
                            <TextAreaField
                                value={explanation}
                                disabled={isSaving}
                                placeholder="Optional context from the conversation"
                                onChange={({ target }) => setExplanation(target.value)}
                            />
                        </div>
                        {error && (
                            <div className="text-md text-red-600 bg-red-50 p-2 rounded">
                                {error}
                            </div>
                        )}
                    </div>
                )}
            </BaseModalMainContent>
            <BaseModalFooter>
                <div className="flex justify-end gap-2">
                    <Button
                        appearance="outlined_primary"
                        onClick={isConfirming ? () => setIsConfirming(false) : onClose}
                        disabled={isSaving}>
                        {isConfirming ? 'No, go back' : 'Cancel'}
                    </Button>
                    <Button
                        appearance="primary"
                        onClick={isConfirming ? onConfirm : onContinue}
                        disabled={isSaving || !reason || !isValidDate}>
                        {(() => {
                            if (isSaving) {
                                return isImmediate ? 'Terminating...' : 'Scheduling...'
                            }

                            if (!isConfirming) {
                                return 'Continue'
                            }

                            return isImmediate ? 'Yes, terminate now' : 'Yes, schedule it'
                        })()}
                    </Button>
                </div>
            </BaseModalFooter>
        </BaseModal>
    )
})

export default ScheduleTerminationModal
